import * as React from 'react';
import { useCallback } from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { formatCompactNumber } from '@antimatterhq/ui/utils/format';
import { Checkbox } from '@antimatterhq/ui/components/checkbox';
import { Input } from '@antimatterhq/ui/components/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@antimatterhq/ui/components/dropdownMenu';
import { Button } from '@antimatterhq/ui/components/button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '../icon/icon';

export interface Field {
  id: string;
  name: string;
  count: number;
  checked: boolean;
  /**
   * When true, the field (column) is locked and cannot be hidden.  This is
   * used to prevent users from hiding the last visible column or any other
   * column that should remain permanently visible.
   */
  disabled?: boolean;
}

export type FieldSortOption =
  | 'name-asc'
  | 'name-desc'
  | 'count-asc'
  | 'count-desc';

interface SortableFieldItemProps {
  field: Field;
  onToggle: () => void;
  isDragEnabled: boolean;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  field,
  onToggle,
  isDragEnabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id, disabled: !isDragEnabled });

  const { disabled = false } = field;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : undefined,
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 hover:bg-grey-50 dark:hover:bg-grey-700',
        isDragging && 'bg-grey-100 dark:bg-grey-700',
      )}
    >
      {isDragEnabled && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-grey-400 active:cursor-grabbing dark:text-grey-500"
        >
          <Icon icon="lucide:grip-vertical" className="h-4 w-4" />
        </div>
      )}
      <label
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        )}
      >
        <Checkbox
          checked={field.checked}
          onCheckedChange={() => {
            if (!disabled) {
              onToggle();
            }
          }}
          disabled={disabled}
        />
        <span className="min-w-0 flex-1 truncate text-sm text-grey-800 dark:text-grey-100">
          {field.name}
        </span>
        <span className="flex-shrink-0 text-sm text-grey-500 dark:text-grey-400">
          {formatCompactNumber(field.count)}
        </span>
      </label>
    </div>
  );
};

export interface FieldListProps {
  fields: Field[];
  onFieldToggle: (fieldId: string) => void;
  onToggleAll: () => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  enableDragAndDrop?: boolean;
  showFilter?: boolean;
  showSort?: boolean;
  filterValue?: string;
  sortOrder?: FieldSortOption | null;
  onFilterChange?: (value: string) => void;
  onSortChange?: (order: FieldSortOption | null) => void;
  className?: string;
  stickyHeader?: boolean;
}

export const FieldList = React.forwardRef<HTMLDivElement, FieldListProps>(
  (
    {
      fields,
      onFieldToggle,
      onToggleAll,
      onReorder,
      enableDragAndDrop = false,
      showFilter = false,
      showSort = false,
      filterValue = '',
      sortOrder = null,
      onFilterChange,
      onSortChange,
      className,
      stickyHeader = false,
    },
    ref,
  ) => {
    const [isFilterOpen, setIsFilterOpen] = React.useState(false);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    // Consider only user-toggleable fields when determining overall state.
    const toggleableFields = React.useMemo(
      () => fields.filter((f) => !f.disabled),
      [fields],
    );

    const allChecked =
      toggleableFields.length > 0 &&
      toggleableFields.every((field) => field.checked);

    const someChecked = toggleableFields.some((field) => field.checked);
    const isIndeterminate = someChecked && !allChecked;

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
          const oldIndex = fields.findIndex((field) => field.id === active.id);
          const newIndex = fields.findIndex((field) => field.id === over?.id);

          if (oldIndex !== -1 && newIndex !== -1) {
            onReorder?.(oldIndex, newIndex);
          }
        }
      },
      [fields, onReorder],
    );

    return (
      <div ref={ref} className={cn('flex flex-col', className)}>
        <div
          className={cn(
            'flex items-center justify-between border-b border-grey-200 bg-white px-2 py-0 dark:border-grey-650 dark:bg-grey-800',
            stickyHeader && 'sticky top-0 z-10',
          )}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              checked={
                allChecked ? true : isIndeterminate ? 'indeterminate' : false
              }
              onCheckedChange={onToggleAll}
              aria-label="Toggle all fields"
            />
            <span className="text-sm font-medium text-grey-800 dark:text-grey-100">
              Fields
            </span>
          </div>
          <div className="flex items-center gap-1">
            {showFilter && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="h-8 w-8 p-0"
              >
                <Icon icon="lucide:search" className="h-4 w-4" />
              </Button>
            )}
            {showSort && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="small" className="h-8 w-8 p-0">
                    <Icon icon="lucide:chevron-down" className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => onSortChange?.('name-asc')}
                    className={cn(
                      sortOrder === 'name-asc' &&
                        'bg-grey-100 dark:bg-gray-600',
                    )}
                  >
                    Name: A → Z
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange?.('name-desc')}
                    className={cn(
                      sortOrder === 'name-desc' &&
                        'bg-grey-100 dark:bg-gray-600',
                    )}
                  >
                    Name: Z → A
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange?.('count-desc')}
                    className={cn(
                      sortOrder === 'count-desc' &&
                        'bg-grey-100 dark:bg-gray-600',
                    )}
                  >
                    Count: High → Low
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange?.('count-asc')}
                    className={cn(
                      sortOrder === 'count-asc' &&
                        'bg-grey-100 dark:bg-gray-600',
                    )}
                  >
                    Count: Low → High
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isFilterOpen && showFilter && (
            <div
              className={cn(
                'border-b border-grey-200 bg-white p-2 dark:border-grey-650 dark:bg-grey-800',
                stickyHeader && 'sticky top-0 z-10',
              )}
            >
              <Input
                placeholder="Filter fields..."
                value={filterValue}
                onChange={(e) => onFilterChange?.(e.target.value)}
                className="h-8"
              />
            </div>
          )}

          <div className="flex flex-col">
            {enableDragAndDrop ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field) => (
                    <SortableFieldItem
                      key={field.id}
                      field={field}
                      onToggle={() => onFieldToggle(field.id)}
                      isDragEnabled={true}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              fields.map((field) => (
                <SortableFieldItem
                  key={field.id}
                  field={field}
                  onToggle={() => onFieldToggle(field.id)}
                  isDragEnabled={false}
                />
              ))
            )}
            {fields.length === 0 && (
              <div className="px-2 py-4 text-center text-sm text-grey-500 dark:text-grey-400">
                No fields found
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

FieldList.displayName = 'FieldList';
